import moment, { Moment } from 'moment';
import React, { useCallback, useMemo } from 'react';

import { Axis } from 'src/components/charts/helpers/Axis';
import {
  useXTimeAxisTicks,
  useYAxisTicks,
} from 'src/components/charts/helpers/axisTicks';
import {
  useLinearScale,
  useTimeScale,
} from 'src/components/charts/helpers/useScale';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';

import { BarColor, Bars } from '../helpers/Bars';
import { CursorPosition } from '../helpers/Cursor';
import { TimeSeriesData } from '../helpers/dataTypes';
import { SvgWrapper, useSvgLayout } from '../helpers/svgLayout';
import { useCursorDateIndex } from '../helpers/useCursorDateIndex';
import { useCursorStackIndex } from '../helpers/useCursorStackIndex';
import { YAxisLabel } from '../helpers/YAxisLabel';

export interface BarChartValue {
  value: number;
  timestamp: Timestamp['output'];
  tooltipContent: string;
  stackCounts?: { value: number; label?: string }[];
}

export type BarChartProps<Data extends TimeSeriesData> = {
  /**
   * The data to be visualized, where the array must contain at least one element.
   *
   * CURRENT CONSTRAINTS:
   * - Timestamps must be in ascending order; descending order is not supported.
   * - Timestamps must be in chronological order; a dataset that is out-of-order is not supported.
   * - Leap years add a day (Feb 29); ensure date ranges and calculations account for this.
   */
  data: MinLengthArray<1, Data>;
  /**
   * Extracts key properties:
   * - `value`: Y-axis value (bar height).
   * - `timestamp`: X-axis value (bar position).
   * - `tooltipContent`: Tooltip text.
   * - `stackCounts`: Optional array of objects representing the breakdown of the value in the bar.
   *    This is required for rendering segmented data in bar charts.
   *    Each object in the array should contain:
   *    - `value`: The numeric portion of the total value that this stack represents.
   *    - `label`: (Optional) A label or description for this stack portion.
   *
   * **Gotchas**:
   * - Ensure `timestamp` is properly formatted for accurate plotting, and is in ascending order.
   * - The `value` must be the final numeric value to visualize.
   *
   * @example
   * ```jsx
   * getValue={(data) => ({
   *   value: data.sales,
   *   timestamp: data.date,
   *   tooltipContent: `${data.sales} units sold`,
   *   stackCounts: [
   *     { value: 5, label: 'Retail' },
   *     { value: 3, label: 'Online' },
   *   ],
   * })}
   * ```
   */
  getValue: (data: Data) => BarChartValue;
  className?: string;
  yAxisLabel?: string;
  barColors?: BarColor[];
  tooltipContent?: React.ReactNode;
  onBarClick?: (barData: Data, cursorStackIndex: number | null) => void;
};

export function BarChart<Data extends TimeSeriesData>({
  data: inputData,
  getValue,
  className,
  yAxisLabel,
  barColors,
  tooltipContent,
  onBarClick,
}: BarChartProps<Data>) {
  const { setMaxYLabelLength, setFirstTick, ...svgLayout } = useSvgLayout({});
  const { height, width: containerWidth, bottom } = svgLayout;

  const { data, maxValue, minX, maxX } = useMemo(() => {
    const firstDate: Moment = moment(getValue(inputData[0]).timestamp);
    let maxY: number = 0;
    let tempMaxX: Moment = firstDate;
    let tempMinX: Moment = firstDate;

    // Map the input data to a format suitable for the chart
    // While mapping, also calculate the minX, maxX, and maxY values
    const chartConfig = inputData.map((d) => {
      const value = getValue(d);
      maxY = Math.max(value.value, maxY);
      const current: Moment = moment(value.timestamp);
      tempMinX = moment.min(tempMinX, current);
      tempMaxX = moment.max(tempMaxX, current);

      return {
        ...value,
        x: current.toDate(),
        y: value.value,
        // `stackCounts` is an array of segments for stacked bar visualization.
        // This allows the bar chart to display multiple values within a single bar.
        stackCounts: value.stackCounts || [
          { value: value.value, label: String(value) },
        ],
        original: d,
      };
    });

    return {
      data: chartConfig,
      maxValue: maxY,
      minX: tempMinX,
      maxX: tempMaxX,
    };
  }, [getValue, inputData]);

  // Adjust x-axis boundaries based on container width and data range
  const { minDate, maxDate, timeAdded } = useMemo(() => {
    // Determine the total time span of the data in seconds
    const dataRange = maxX.diff(minX, 'seconds');

    let computedTimeAdded;

    if (dataRange === 0) {
      // If all timestamps are the same, we set a default padding time.
      // This ensures that the chart still renders correctly even with identical timestamps.
      computedTimeAdded = 60;
    } else {
      // Calculate padding to add based on data range and bar width
      computedTimeAdded = dataRange / (inputData.length - 1) / 2;
    }

    // Adjust x-axis boundaries by the computed padding
    const adjustedMinX = minX.clone().subtract(computedTimeAdded, 'seconds');
    const adjustedMaxX = maxX.clone().add(computedTimeAdded, 'seconds');

    return {
      minDate: adjustedMinX.toDate(), // New min x-axis boundary
      maxDate: adjustedMaxX.toDate(), // New max x-axis boundary
      timeAdded: computedTimeAdded, // Time added for padding
    };
  }, [inputData.length, maxX, minX]);

  const yAxisTicks = useYAxisTicks({ height, maxValue, setMaxYLabelLength });

  // Define x-axis scale using the adjusted date boundaries
  const xScale = useTimeScale({
    range: [0, containerWidth],
    domain: [minDate, maxDate],
  });

  // Identify the index of the data point closest to the cursor position
  const cursorValueIndex = useCursorDateIndex({
    data,
    getTimestamp: (bucket: { timestamp: Timestamp['output'] }) =>
      bucket.timestamp,
  });

  // Identify the index of the data point closest to the cursor position
  const cursorStackIndex = useCursorStackIndex({
    datapoint: cursorValueIndex !== null ? data[cursorValueIndex] : undefined,
  });

  // Determine the first tick on the x-axis, adding padding if necessary
  const xAxisFirstTickValue = useMemo(
    () => moment(minDate).add(timeAdded, 'seconds'),
    [minDate, timeAdded],
  );

  // Generate the x-axis ticks, considering cursor position and tick intervals
  const {
    ticks: xTicks,
    cursorTick,
    hiddenTickIndexs,
  } = useXTimeAxisTicks({
    scale: xScale,
    width: containerWidth,
    cursorTimestamp:
      (cursorValueIndex !== null && data[cursorValueIndex]?.timestamp) || null,
    firstTickValue: xAxisFirstTickValue,
    tickIntervalMinutes: moment.duration(timeAdded * 2, 'seconds').asMinutes(),
    firstDate: data[0]?.timestamp,
    setFirstTick,
  });

  const yScale = useLinearScale({ range: [height, 0], domain: [0, maxValue] });
  const xyToCursorPosition = useCallback(
    (xy: [number, number]): CursorPosition => ({
      date: moment(xScale.invert(xy[0])),
      yValue: yScale.invert(xy[1]),
    }),
    [xScale, yScale],
  );
  return (
    <div className={className}>
      <div className="flex size-full">
        <div className="flex-grow">
          <SvgWrapper
            className="size-full"
            getCursor={xyToCursorPosition}
            {...svgLayout}
          >
            <Axis
              tickValues={xTicks}
              cursorTick={cursorTick}
              hiddenTicks={hiddenTickIndexs}
              scale={xScale}
              y={height}
              side="bottom"
              cursorContrastMode="high"
            />
            <Axis
              withLines={true}
              tickValues={yAxisTicks}
              scale={yScale}
              x={containerWidth}
              hideDomain
              side="right"
            />
            <Bars
              height={height}
              width={containerWidth}
              xScale={xScale}
              yScale={yScale}
              cursorValueIndex={cursorValueIndex}
              cursorStackIndex={cursorStackIndex}
              data={data}
              barColors={barColors}
              barWidthPercent={0.42}
              getTooltip={(value) => tooltipContent ?? value.tooltipContent}
              onClick={(barData, stackIndex) =>
                onBarClick?.(barData.original, stackIndex)
              }
            />
          </SvgWrapper>
        </div>
        {yAxisLabel ? (
          <YAxisLabel height={height - bottom} value={yAxisLabel} />
        ) : null}
      </div>
    </div>
  );
}
